import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAuthenticated, isFullyAuthenticated, logout, softSignOut } from '../lib/auth';
import { useLockContext } from '../contexts/LockContext';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import { useSessionValidator } from '../hooks/useSessionValidator';
import LockScreen from './LockScreen';
import PasskeyRegistrationModal from './PasskeyRegistrationModal';
import { BIOMETRICS_DISABLED } from '../config/env';
import { hasExceededOfflineLimit } from '../db/services/offline_time_tracker';
import { useServerOnline } from '../hooks/useServerOnline';
import { createLogger, LogLevel } from '../lib/logger';
import { NoSymbolIcon } from '@heroicons/react/24/outline';

const logger = createLogger('AuthWrapper', LogLevel.DEBUG);

interface AuthWrapperProps {
  children: React.ReactNode;
}

/**
 * Authentication wrapper that protects routes by checking for auth_token
 * Redirects to login page if not authenticated
 */
export default function AuthWrapper({ children }: AuthWrapperProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [noOfflineAccess, setNoOfflineAccess] = useState(false);
  const { isLocked, isRegistered, unlockScreen, isLoading: lockLoading } = useLockContext();

  // Reactively watch currentPerson — if it gets cleared (e.g., by a 401 handler
  // or session validator), redirect to login/org selection immediately.
  const { currentPerson, loading: personLoading } = useCurrentPerson();

  // Validate the server session on tab focus / online events.
  // This detects when a different user logged in on the online app (shared cookie).
  useSessionValidator();

  // Track online status reactively so the offline-access gate re-evaluates on connectivity changes
  const online = useServerOnline();

  // React to currentPerson being cleared while the user was already authenticated.
  // IMPORTANT: We verify via a direct DB query before redirecting, because
  // useLiveQuery can lag behind the actual IndexedDB state. Without this,
  // navigating from /select-organization to / (immediately after setCurrentPerson)
  // can trigger a false redirect — useLiveQuery still returns null even though
  // the person was already written to the DB.
  useEffect(() => {
    if (personLoading || isLoading) return;
    if (!isAuthed) return;
    // Never redirect away from the lock screen: when the DB is closed for
    // locking, useLiveQuery transiently returns null which previously caused a
    // redirect-then-return loop and the lock screen to visibly flash.
    if (isLocked) return;

    // Skip for pages that don't require auth
    const publicPaths = ['/login', '/auth/callback', '/select-organization', '/offline-expired'];
    if (publicPaths.includes(location.pathname)) return;

    if (currentPerson !== null) return;

    // currentPerson is null according to useLiveQuery — verify with a direct
    // DB read before redirecting. Two distinct staleness sources to guard:
    //   (a) useLiveQuery hasn't yet observed a recent setCurrentPerson write,
    //   (b) `isAuthed` was set to true by checkAuth's lock-screen short-circuit
    //       (line below in checkAuth) without verifying an actual session.
    // For (b), defaulting to /select-organization here would beat checkAuth's
    // /login redirect and produce a wrong-target navigation on every cold mount
    // where the lock state flips after the initial render. So we re-verify the
    // session via isAuthenticated() and pick the destination ourselves.
    //
    // allSettled (not Promise.all) so a rejection in one read can't drop the
    // result of the other — in particular, if isAuthenticated() were ever
    // wired to throw (today the service layer swallows errors and returns
    // false, but that's an implementation detail we shouldn't rely on),
    // we'd still want to no-op when isFullyAuthenticated() reports the
    // person is actually set.
    //
    // Decision table for the four meaningful outcomes:
    //   authed=true,  fullyAuthed=true    → no-op  (useLiveQuery was lagging)
    //   authed=true,  fullyAuthed=false   → /select-organization (original intent)
    //   authed=false, *                   → /login (offline-first: don't assume
    //                                       current_user_id clears paired with
    //                                       current_person — session expiry can
    //                                       clear only the session and leave
    //                                       unsaved local work intact)
    //   either rejected                   → /login (pathological state; safer to
    //                                       send to a public route than to leave
    //                                       the user on a blank protected page)
    let cancelled = false;

    Promise.allSettled([isAuthenticated(), isFullyAuthenticated()]).then(([authedResult, fullyAuthedResult]) => {
      if (cancelled) return;

      if (authedResult.status === 'rejected' || fullyAuthedResult.status === 'rejected') {
        logger.warn('Auth check failed in person-cleared effect — falling back to /login', {
          authedResult,
          fullyAuthedResult,
        });
        setIsAuthed(false);
        navigate('/login');
        return;
      }

      const authed = authedResult.value;
      const fullyAuthed = fullyAuthedResult.value;

      if (authed && fullyAuthed) return;

      if (!authed) {
        logger.info('No session — redirecting to login');
        setIsAuthed(false);
        navigate('/login');
        return;
      }

      logger.info('Person cleared — redirecting to org selection');
      const returnTo = encodeURIComponent(location.pathname || '/');
      setIsAuthed(false);
      navigate(`/select-organization?return_to=${returnTo}`);
    });

    return () => { cancelled = true; };
  }, [currentPerson, personLoading, isAuthed, isLoading, isLocked, location.pathname, navigate]);

  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check for login page, auth callback, organization selection, and offline-expired page
      if (location.pathname === '/login' || location.pathname === '/auth/callback' || location.pathname === '/select-organization' || location.pathname === '/offline-expired') {
        setIsLoading(false);
        setIsAuthed(true);
        return;
      }

      // When the screen is locked the encrypted DB is closed. Running the
      // full auth pipeline (isFullyAuthenticated / hasExceededOfflineLimit)
      // against a closed DB produces spurious "not authenticated" results,
      // which redirects away from the lock screen and causes a flashing
      // loop. Skip auth checks here: the `if (!BIOMETRICS_DISABLED && isLocked)
      // return <LockScreen />` gate below blocks all access to children
      // until the user unlocks, so a transient `isAuthed = true` cannot
      // leak protected content. The full pipeline re-runs after unlock
      // when `isLocked` flips back to false (it's a dependency of this
      // effect). If that render gate is ever removed, this short-circuit
      // must be revisited — and so must the person-cleared effect above,
      // which only trusts `isAuthed` after re-verifying via isAuthenticated()
      // precisely because of this transient `isAuthed = true` window.
      if (isLocked) {
        setIsAuthed(true);
        setIsLoading(false);
        return;
      }

      // Check if user has established a session via auth callback
      const hasSession = await isAuthenticated();

      if (!hasSession) {
        setIsLoading(false);
        setIsAuthed(false);
        navigate('/login');
        return;
      }

      // Check if user has selected a person
      const fullyAuthenticated = await isFullyAuthenticated();

      if (!fullyAuthenticated) {
        // User has session but no person selected - redirect to organization selection
        // Preserve the current URL as return_to parameter
        const returnTo = encodeURIComponent(location.pathname || '/');
        setIsLoading(false);
        setIsAuthed(false);
        navigate(`/select-organization?return_to=${returnTo}`);
        return;
      }

      // Check if offline time limit has been exceeded (30 days)
      const offlineExpired = await hasExceededOfflineLimit();
      if (offlineExpired) {
        setIsLoading(false);
        setIsAuthed(false);
        navigate('/offline-expired');
        return;
      }

      // After successful authentication, check if passkey is registered
      // Skip this check if biometrics is disabled (e.g., for testing)
      if (!BIOMETRICS_DISABLED && !lockLoading && !isRegistered) {
        setShowRegistration(true);
      }

      setIsAuthed(true);
      setIsLoading(false);
    };

    // checkAuth is fire-and-forget; route any unexpected rejection to /login
    // for the same reason the person-cleared effect's catch branch does:
    // a thrown DB read (e.g. transient DatabaseClosedError) shouldn't leave
    // the user on a protected route as a blank page with no way out. Today
    // the service layer swallows IDB errors, so this catch is a safety net,
    // not a regular code path.
    checkAuth().catch((error) => {
      logger.warn('checkAuth failed — falling back to /login', error);
      setIsLoading(false);
      setIsAuthed(false);
      navigate('/login');
    });
  }, [navigate, location.pathname, lockLoading, isRegistered, isLocked]);

  // Check offline permission when person data is available and online
  useEffect(() => {
    if (personLoading || !currentPerson) return;
    if (currentPerson.has_offline_access === false && online) {
      setNoOfflineAccess(true);
    } else {
      setNoOfflineAccess(false);
    }
  }, [currentPerson, personLoading, online]);

  // Show loading spinner while checking auth or lock context
  if (isLoading || lockLoading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Don't render children if not authenticated (will redirect)
  if (!isAuthed) {
    return null;
  }

  // Show error when user lacks offline app permission and is online
  if (noOfflineAccess) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-8 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <NoSymbolIcon className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Access Not Permitted</h2>
          <p className="text-gray-600 mb-6">
            You don&apos;t have permission to use Mon Ami GO. Please contact your administrator to request access.
          </p>
          <button
            onClick={() => softSignOut()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Show registration modal if not registered (mandatory)
  // Skip if biometrics is disabled (e.g., for testing)
  if (!BIOMETRICS_DISABLED && showRegistration && !isRegistered) {
    return (
      <PasskeyRegistrationModal
        isRequired={true}
        onComplete={() => setShowRegistration(false)}
        onCancel={() => {
          // Can't cancel if required - logout instead
          logout();
        }}
      />
    );
  }

  // Show lock screen if locked
  // Skip if biometrics is disabled (e.g., for testing)
  if (!BIOMETRICS_DISABLED && isLocked) {
    return (
      <LockScreen
        onUnlock={unlockScreen}
        onLogout={logout}
      />
    );
  }

  return <>{children}</>;
}
